import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Select,
  Skeleton,
} from '@marrafa-gioale/gymx-ui-web'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Bot, ChevronDown, X } from 'lucide-react'
import { z } from 'zod'

import { ApiError, errorMessage } from '@/api/errors'
import { useAutomationRules, useAutomationRuns } from '@/api/queries'
import type { AutomationOutcome, AutomationRun } from '@/api/types'
import { AUTOMATION_OUTCOMES } from '@/api/types'
import { PageHeader } from '@/components/layout/app-shell'
import { MessageTemplatesSection } from '@/features/crm/message-templates-section'
import { AutomationRulesSection } from '@/features/crm/automation-rules-section'
import { formatDateTime, formatRelative, plural } from '@/lib/format'

/**
 * Cosa ha fatto il CRM da sé — CRM-05.
 *
 * ## È una pagina di sola lettura, e non è un pezzo mancante
 *
 * Il motore ha **cinque regole cablate** e non si configura da nessuna parte: l'ordine è
 * dichiarato, e dice che le occorrenze e il log vengono **prima** di qualsiasi configurabilità
 * — disegnare l'interfaccia di configurazione di un motore che non ha ancora eseguito niente
 * significa disegnare le domande sbagliate. L'editor è CRM-06, e le domande gliele porrà
 * questa pagina.
 *
 * Che è anche la ragione per cui questa pagina viene **prima** di quell'editor: fino a ieri
 * cinque regole giravano ogni quarto d'ora in produzione e il loro log si poteva leggere solo
 * con `curl`. Un motore di automazioni di cui non si vede l'effetto è indistinguibile da un
 * guasto — ed è la stessa frase che il contract usa per spiegare perché quel log esiste.
 *
 * ## Una riga risponde a «perché è partita questa cosa?»
 *
 * Non a «cosa è partito», che è la domanda facile. Ogni riga porta la **regola in parole**
 * (con la soglia dentro: «fra 7 giorni», perché quella soglia cambierà e un log che dicesse
 * solo «scadenza» costringerebbe a indovinare quale fosse allora), **il valore che ha reso
 * vera la condizione**, l'esito, e il nome della persona che porta alla sua scheda — dove
 * stanno il messaggio ricevuto e la cronologia.
 *
 * ## Gli esiti non sono quattro sfumature di errore
 *
 * `done` è fatto; `skipped` **non è un guasto** — è una persona di cui non abbiamo
 * l'indirizzo, o che ha chiesto di non ricevere pubblicità, ed è la riga che risponde a «perché
 * non le arriva niente?»; `failed` è il mailer che ha detto no; `pending` è il giro morto fra
 * la prenotazione e l'azione, e **non si ritenta da sé**. Quest'ultimo è l'unico che chiede di
 * fare qualcosa, quindi è l'unico rosso.
 *
 * ## Nessuna paginazione numerata
 *
 * La rotta non manda `meta.total` — come il registro — quindi «pagina N di M» non esiste e
 * «carica altro» è l'unica forma onesta. Le righe caricate restano a schermo: confrontare due
 * occorrenze lontane è metà del lavoro.
 */
const searchSchema = z.object({
  ruleId: z.string().optional(),
  outcome: z.enum(AUTOMATION_OUTCOMES).optional(),
})

export const Route = createFileRoute('/_app/automazioni/')({
  validateSearch: searchSchema,
  component: AutomationsPage,
})

const OUTCOME_LABELS: Record<AutomationOutcome, string> = {
  done: 'Fatto',
  skipped: 'Non fatto, con un motivo',
  failed: 'Fallito',
  pending: 'Interrotto a metà',
}

const OUTCOME_VARIANTS: Record<AutomationOutcome, 'success' | 'neutral' | 'warning' | 'danger'> = {
  done: 'success',
  // Non è un guasto: è la riga che spiega perché a qualcuno non arriva niente.
  skipped: 'neutral',
  failed: 'warning',
  // L'unico che chiede di fare qualcosa: il giro è morto fra la prenotazione e l'azione.
  pending: 'danger',
}

const ACTION_LABELS: Record<AutomationRun['action'], string> = {
  message: 'Email',
  task: 'Attività per un operatore',
  tag: 'Etichetta',
}

function AutomationsPage() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  // Le regole per il filtro arrivano dalla **stessa** rotta che le mostra sopra: cablarle qui
  // avrebbe significato che una regola aggiunta al motore compare nelle righe del log ma non
  // nell'elenco con cui filtrarle.
  const ruleCatalog = useAutomationRules()
  const ruleOptions = (ruleCatalog.data?.data ?? []).map((rule) => ({
    value: rule.ruleId,
    label: rule.ruleLabel,
  }))
  const list = useAutomationRuns({
    ...(search.ruleId === undefined ? {} : { ruleId: search.ruleId }),
    ...(search.outcome === undefined ? {} : { outcome: search.outcome }),
    limit: 50,
  })

  const rows = list.data?.pages.flatMap((page) => page.data) ?? []
  const filtered = search.ruleId !== undefined || search.outcome !== undefined

  return (
    <>
      <PageHeader
        title="Automazioni"
        description="Cosa ha fatto il CRM da sé: quale regola, per chi, e cosa ne è seguito."
      />

      {/* Le regole **sopra** il log, nella stessa pagina: la domanda che porta qui è «perché è
          partita questa cosa?», e la risposta e il rimedio sono lo stesso gesto — si legge una
          riga del registro e si spegne o si riscrive la regola che l'ha prodotta. In due pagine
          separate avrebbe voluto dire ricordare quale regola si stava guardando mentre si cercava
          dove cambiarla. */}
      <AutomationRulesSection />

      {/* I modelli **dopo** le regole e prima del registro: una regola nomina un modello,
          e il registro dice cosa quel modello ha prodotto. È l'ordine in cui si legge la
          catena quando qualcuno chiede «perché è arrivato questo messaggio?». */}
      <MessageTemplatesSection />

      <h2 className="mb-3 text-base font-semibold">Cosa è stato fatto</h2>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label="Regola" className="min-w-52">
          {(props) => (
            <Select
              {...props}
              value={search.ruleId}
              placeholder="Tutte"
              items={ruleOptions}
              onValueChange={(value) => {
                void navigate({ search: (prev) => ({ ...prev, ruleId: value }) })
              }}
            />
          )}
        </Field>
        <Field label="Esito" className="min-w-44">
          {(props) => (
            <Select
              {...props}
              value={search.outcome}
              placeholder="Tutti"
              items={AUTOMATION_OUTCOMES.map((outcome) => ({
                value: outcome,
                label: OUTCOME_LABELS[outcome],
              }))}
              onValueChange={(value) => {
                void navigate({
                  search: (prev) => ({ ...prev, outcome: value as AutomationOutcome }),
                })
              }}
            />
          )}
        </Field>
        {filtered ? (
          <Button
            variant="ghost"
            onClick={() => {
              void navigate({ search: {} })
            }}
          >
            <X />
            Togli i filtri
          </Button>
        ) : null}
      </div>

      {list.isError ? (
        <ErrorState
          message={errorMessage(list.error)}
          requestId={list.error instanceof ApiError ? list.error.requestId : undefined}
          onRetry={() => void list.refetch()}
        />
      ) : list.isPending ? (
        <div className="space-y-2">
          {[0, 1, 2, 3, 4].map((row) => (
            <Skeleton key={row} className="h-20 w-full" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Bot />}
          title={
            filtered
              ? 'Nessuna occorrenza con questi filtri'
              : 'Il motore non ha ancora fatto niente'
          }
          description={
            filtered
              ? 'Le altre occorrenze ci sono: togli i filtri per vederle.'
              : 'Le regole girano col giro di manutenzione. Una riga comparirà appena una condizione diventa vera per qualcuno — un abbonamento che scade fra sette giorni, un compleanno, un mese senza ingressi.'
          }
        />
      ) : (
        <>
          <ul className="flex flex-col gap-2">
            {rows.map((run) => (
              <li key={run.id}>
                <Card radius="md" padding="sm">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium">{run.ruleLabel}</p>
                      <p className="text-muted-foreground text-sm">
                        {/* Il nome porta alla scheda: è lì che stanno il messaggio ricevuto e la
                          cronologia, cioè il resto della risposta. */}
                        <Link
                          to="/iscritti/$gymMemberId"
                          params={{ gymMemberId: run.subjectId }}
                          className="text-secondary-content font-medium underline-offset-4 hover:underline"
                        >
                          {run.subjectName}
                        </Link>
                        {run.matchedValue === null ? null : <> · {run.matchedValue}</>}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Badge variant="outline">{ACTION_LABELS[run.action]}</Badge>
                      <Badge variant={OUTCOME_VARIANTS[run.outcome]}>
                        {OUTCOME_LABELS[run.outcome]}
                      </Badge>
                    </div>
                  </div>
                  <p className="text-muted-foreground mt-2 text-xs">
                    {formatDateTime(run.createdAt)} · {formatRelative(run.createdAt)}
                    {typeof run.detail.failureReason === 'string' ? (
                      <> · {run.detail.failureReason}</>
                    ) : null}
                  </p>
                </Card>
              </li>
            ))}
          </ul>

          <div className="flex flex-col items-center gap-2 pt-4">
            {list.hasNextPage ? (
              <Button
                variant="outline"
                loading={list.isFetchingNextPage}
                onClick={() => void list.fetchNextPage()}
              >
                <ChevronDown />
                Carica altre occorrenze
              </Button>
            ) : (
              <p className="text-muted-foreground text-xs">Fine del registro per questi filtri.</p>
            )}
            <p className="text-muted-foreground text-xs" data-numeric>
              {plural(rows.length, 'occorrenza caricata', 'occorrenze caricate')}
            </p>
          </div>
        </>
      )}
    </>
  )
}
